import { SpaceBetween, ColumnLayout, Box } from '@amzn/awsui-components-console';
import React from 'react';

import keys from '../../constants/hammerstoneConstantKeys';
import { DisplayMode } from '../helpers/content/contentInterfaces';
import AboutContainer from './all/AboutContainer';
import NotificationsContainer from './all/NotificationsContainer';
import PreconditionsContainer from './all/PreconditionsContainer';
import ScheduleContainer from './all/ScheduleContainer';
import ExtractTransformS3Container from './all/ExtractTransformS3Container';
import ExtractSQLContainer from './extract/ExtractSQLContainer';
import LoadConfigContainer from './load/LoadConfigContainer';
import LoadJSONContainer from './load/LoadJSONContainer';
import LoadS3Container from './load/LoadS3Container';
import LoadSQLContainer from './load/LoadSQLContainer';
import TransformAuditorContainer from './transform/TransformAuditorContainer';
import TransformConfigContainer from './transform/TransformConfigContainer';
import TransformSQLContainer from './transform/TransformSQLContainer';
import ErrorLayout from '../error/ErrorLayout';
import ErrorCannotCreateOdinActivity from '../error/ErrorCannotCreateOdinActivity';
import { useS3CredentialType, useActivity, useFormField, useGroupName } from 'src/data/redux';
import Activity from 'src/interfaces/activityInterfaces';
import PAGES from 'src/nav/Pages';
import InternalLink from '../helpers/InternalLink';

export default function ActivityContainerLayout(props: {
	mode: DisplayMode;
	activityId: string;
	editAction?: keyof typeof keys.ActivityFormActions;
	activityType?: string;
	diffActivityId?: string;
}) {
	const { activityType, activityId } = props;
	const { data: activity, fetching } = useActivity(activityId);

	const containerProps = {
		mode: fetching ? DisplayMode.Loading : props.mode,
		activityId,
		editAction: props.editAction,
		diffActivityId: props.diffActivityId,
	};

	const groupName = useGroupName();

	const pipelineName = useFormField<Activity>('activityNamespace');
	const s3CredentialType = useS3CredentialType({ pipelineName, groupName });

	// When creating a new resource (e.g. Activity), include a blank/undefined dataId
	const newActivity = ['create', 'copy'].includes(props.editAction);

	if (!newActivity && !activity && containerProps.mode !== DisplayMode.Loading) {
		return (
			<ErrorLayout header="Failed to fetch activity" backtrackButtons>
				<SpaceBetween size="m">
					<Box>
						We apologize for the inconvenience, but there was an error while fetching activity {activityId}.
					</Box>
					<Box>
						If this activity was recently edited and is now unreachable (e.g. error message like "null",
						"could not retrieve configuration metadata", or "activity may be corrupted") then you may need
						to roll back to a previous revision. To do so, navigate to{' '}
						<InternalLink href={PAGES.ACTIVITY_HISTORY.path} pathParams={{ activityId }} groupName>
							this activity's history
						</InternalLink>
						, select the previous revision, and click "Revert".
					</Box>
				</SpaceBetween>
			</ErrorLayout>
		);
	} else {
		return (
			<SpaceBetween size="xl">
				<AboutContainer header="About" {...containerProps} />

				{newActivity && s3CredentialType === 'ODIN' ? (
					// Users should not create or copy activities in Odin pipelines
					// If they attempt to, they will see this error instead of the activity form fields
					<ErrorCannotCreateOdinActivity pipelineName={pipelineName} />
				) : (
					<>
						<ColumnLayout columns={props.mode === 'view' ? 2 : undefined}>
							<ScheduleContainer header="Schedule" {...containerProps} />
							<NotificationsContainer header="Notifications" {...containerProps} />
						</ColumnLayout>
						<PreconditionsContainer header="Preconditions" {...containerProps} />

						{activityType === keys.ActivityType.EXTRACT && (
							<SpaceBetween size="xl">
								<ExtractTransformS3Container
									header="Extract S3"
									activityType="EXTRACT"
									{...containerProps}
								/>
								<ExtractSQLContainer header="Extract SQL" {...containerProps} />
							</SpaceBetween>
						)}

						{activityType === keys.ActivityType.TRANSFORM && (
							<SpaceBetween size="xl">
								<ExtractTransformS3Container
									header="Transform S3"
									activityType="TRANSFORM"
									{...containerProps}
								/>
								<TransformConfigContainer header="Transform configuration" {...containerProps} />
								<TransformSQLContainer header="Transform SQL" {...containerProps} />
								<TransformAuditorContainer header="Auditor" {...containerProps} />
							</SpaceBetween>
						)}

						{activityType === keys.ActivityType.COPY && (
							<SpaceBetween size="xl">
								<LoadS3Container header="Load S3" {...containerProps} />
								<LoadConfigContainer header="Load configuration" {...containerProps} />
								<LoadSQLContainer header="Load SQL" {...containerProps} />
								<LoadJSONContainer header="Load JSON" {...containerProps} />
							</SpaceBetween>
						)}
					</>
				)}
			</SpaceBetween>
		);
	}
}
